// DeviceListView.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Tooltip,
} from "@mhome/ui";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import EntityFilter from "./entity-filter";
import EntityDetailsDialog from "./entity-dialog";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { toEntityTitleSubtitle } from "../common/string-helpers";
import { getRoomList, moveDevice } from "../service/room-service";
import { useWebSocket } from "../ws/websocket";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import LoadingSpinner from "../common/loading";
import { useToast } from "../common/toast";
import MenuCard from "../component/menu-card";
import { useMediaQuery } from "@mui/material";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-1px); }
    20%, 40%, 60%, 80% { transform: translateX(1px); }
  }
`;

const EntityCard = ({
  meta,
  entity,
  getComponentBgColor,
  onSelect,
  onEnterMultiSelect,
  onShowMoveDialog,
  isMultiSelectMode,
  isSelected,
  onCheckboxChange,
  onLongPress,
  isShaking,
}) => {
  const entityInfo = toEntityTitleSubtitle(entity.name, true);
  const displayTitle =
    typeof entityInfo === "object" ? entityInfo.title : entityInfo;
  const displaySubtitle =
    typeof entityInfo === "object"
      ? entityInfo.subtitle
      : meta.integrationNames[entity.integration] || entity.integration;

  const menuItems = [
    {
      label: "Multi Select",
      icon: <CheckBoxOutlineBlankIcon fontSize="small" />,
      onClick: (e) => {
        e.stopPropagation();
        onEnterMultiSelect(entity);
      },
    },
    {
      label: "Move",
      icon: <SwapHorizIcon fontSize="small" />,
      onClick: (e) => {
        e.stopPropagation();
        onShowMoveDialog(entity);
      },
    },
  ];

  return (
    <MenuCard
      menuItems={menuItems}
      onCardClick={() => onSelect(entity)}
      onLongPress={onLongPress}
      sx={{
        height: 90,
        border: "none",
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        "&:hover": {
          backgroundColor: "hsl(var(--accent))",
        },
        "@media (hover: none)": {
          "&:hover": {
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
          },
        },
        animation: isShaking ? "shake 0.5s ease-in-out" : "none",
      }}
      elevation={0}
    >
      <CardContent className="p-3 h-full flex flex-col justify-center relative">
        {isMultiSelectMode && (
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onCheckboxChange(entity);
            }}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 right-0 z-10"
          />
        )}
        <Typography
          variant="subtitle2"
          className="font-medium text-sm leading-tight line-clamp-2 h-[2.4em]"
        >
          {displayTitle}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          noWrap
          className="mt-1"
        >
          {displaySubtitle}
        </Typography>
      </CardContent>
    </MenuCard>
  );
};

const DeviceListView = ({
  meta,
  entities,
  showFilter,
  isChatSidebarVisible = false,
}) => {
  const [entityFilterText, setEntityFilterText] = useState("");
  const [entitySortBy, setEntitySortBy] = useState("alphabet");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deviceRoomMap, setDeviceRoomMap] = useState({});
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [deviceToMove, setDeviceToMove] = useState(null);
  const [shakingCardId, setShakingCardId] = useState(null);
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);
  const { successToast, errorToast } = useToast();
  const isMdOrLgScreen = useMediaQuery("@media (min-width:960px)");

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Fetch room list
  const fetchRooms = async () => {
    if (wsReady) {
      try {
        setLoading(true);
        const roomList = await getRoomList(sendAndWaitWs);
        console.log("roomList", roomList);
        if (roomList && roomList.rooms && Array.isArray(roomList.rooms)) {
          setRooms(roomList.rooms);

          // Build a map of integration+id -> roomId
          const deviceMap = {};
          roomList.rooms.forEach((room) => {
            if (room.devices && Array.isArray(room.devices)) {
              room.devices.forEach((device) => {
                const deviceKey = `${device.integration}:${device.id}`;
                deviceMap[deviceKey] = room.roomId;
              });
            }
          });
          setDeviceRoomMap(deviceMap);
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [wsReady]);

  const handleMoveDevice = async (entity, toRoomId) => {
    const deviceKey = `${entity.integration}:${entity.id}`;
    const fromRoomId = deviceRoomMap[deviceKey] || null;
    try {
      const items = [
        {
          fromRoomId: fromRoomId,
          integration: entity.integration,
          deviceId: entity.id,
        },
      ];
      const response = await moveDevice(sendAndWaitWs, toRoomId, items);
      if (response) {
        successToast("Device moved successfully");
        // Refresh room list to update device assignments
        await fetchRooms();
      } else {
        errorToast("Failed to move device");
      }
    } catch (error) {
      errorToast("Error moving device");
      console.error("Move device error:", error);
    }
  };

  const handleEnterMultiSelect = (entity) => {
    setIsMultiSelectMode(true);
    const deviceKey = `${entity.integration}:${entity.id}`;
    setSelectedDevices([deviceKey]);
  };

  const handleCancelMultiSelect = () => {
    setIsMultiSelectMode(false);
    setSelectedDevices([]);
  };

  const handleCheckboxChange = (entity) => {
    const deviceKey = `${entity.integration}:${entity.id}`;
    setSelectedDevices((prev) => {
      if (prev.includes(deviceKey)) {
        return prev.filter((key) => key !== deviceKey);
      } else {
        return [...prev, deviceKey];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === filteredEntities.length) {
      setSelectedDevices([]);
    } else {
      const allDeviceKeys = filteredEntities.map(
        (entity) => `${entity.integration}:${entity.id}`
      );
      setSelectedDevices(allDeviceKeys);
    }
  };

  const handleShowMoveDialog = (entity) => {
    setDeviceToMove(entity);
    setMoveDialogOpen(true);
  };

  const handleMoveDialogClose = () => {
    setMoveDialogOpen(false);
    setDeviceToMove(null);
  };

  const handleMoveToRoom = async (toRoomId) => {
    if (deviceToMove) {
      await handleMoveDevice(deviceToMove, toRoomId);
      handleMoveDialogClose();
    }
  };

  const handleBatchMove = async (toRoomId) => {
    try {
      const items = selectedDevices.map((deviceKey) => {
        const [integration, id] = deviceKey.split(":");
        const fromRoomId = deviceRoomMap[deviceKey] || null;
        return {
          fromRoomId: fromRoomId,
          integration: integration,
          deviceId: id,
        };
      });

      const response = await moveDevice(sendAndWaitWs, toRoomId, items);
      if (response) {
        successToast(`${items.length} device(s) moved successfully`);
        await fetchRooms();
        handleCancelMultiSelect();
      } else {
        errorToast("Failed to move devices");
      }
    } catch (error) {
      errorToast("Error moving devices");
      console.error("Batch move error:", error);
    }
  };

  const handleCardLongPress = (entity) => {
    const deviceKey = `${entity.integration}:${entity.id}`;
    setShakingCardId(deviceKey);
    setTimeout(() => setShakingCardId(null), 500);
  };

  // Moved the filtering and sorting logic into a useMemo to optimize performance
  const filteredEntities = useMemo(() => {
    return entities.filter((entity) => {
      // Text filter
      const searchTerms = entityFilterText
        .toLowerCase()
        .split(" ")
        .filter((term) => term.length > 0);
      const searchableText = entity.name.toLowerCase();
      const matchesText = !entityFilterText
        ? true
        : searchTerms.every((term) => searchableText.includes(term));

      // Integration filter
      const matchesIntegration =
        selectedIntegrations.length === 0 ||
        selectedIntegrations.includes(entity.integration);

      // Capability filter
      const matchesCapability =
        selectedCapabilities.length === 0 ||
        (entity.capabilities &&
          selectedCapabilities.some((capability) =>
            entity.capabilities.includes(capability)
          ));

      return matchesText && matchesIntegration && matchesCapability;
    });
  }, [entities, entityFilterText, selectedIntegrations, selectedCapabilities]);

  // Group devices by room
  const devicesByRoom = useMemo(() => {
    const grouped = {};

    // Initialize with all rooms
    rooms.forEach((room) => {
      grouped[room.roomId] = {
        roomId: room.roomId,
        roomName: room.roomName,
        devices: [],
      };
    });

    // Add a special category for devices without a room
    grouped["no-room"] = {
      roomId: "no-room",
      roomName: "Unassigned",
      devices: [],
    };

    // Group devices by room using deviceRoomMap
    filteredEntities.forEach((entity) => {
      const deviceKey = `${entity.integration}:${entity.id}`;
      const roomId = deviceRoomMap[deviceKey] || "no-room";
      if (!grouped[roomId]) {
        grouped[roomId] = {
          roomId: roomId,
          roomName: roomId,
          devices: [],
        };
      }
      grouped[roomId].devices.push(entity);
    });

    // Sort devices within each room
    Object.keys(grouped).forEach((roomId) => {
      grouped[roomId].devices.sort((a, b) => {
        if (entitySortBy === "alphabet") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        }
        return 0;
      });
    });

    return grouped;
  }, [filteredEntities, rooms, entitySortBy, deviceRoomMap]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <GlobalStyle />
      <div className="pb-10">
        {showFilter && (
          <EntityFilter
            sortBy={entitySortBy}
            setSortBy={setEntitySortBy}
            setFilterText={setEntityFilterText}
            meta={meta}
            selectedIntegrations={selectedIntegrations}
            setSelectedIntegrations={setSelectedIntegrations}
            selectedCapabilities={selectedCapabilities}
            setSelectedCapabilities={setSelectedCapabilities}
            entities={entities}
          />
        )}

        {/* Multi-select mode bottom bar */}
        {isMultiSelectMode && (
          <div
            className="fixed left-0 bottom-0 px-4 py-3 flex justify-between items-center z-[2000] shadow-lg"
            style={{
              width:
                isMdOrLgScreen && isChatSidebarVisible
                  ? "calc(100vw - 400px)"
                  : "100vw",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            {/* Left side - Count */}
            <div className="flex items-center">
              <Typography variant="body1" className="font-medium">
                {`${selectedDevices.length} selected`}
              </Typography>
            </div>

            {/* Right side - Checkbox and Action buttons */}
            <div className="flex items-center gap-2">
              <Checkbox
                indeterminate={
                  selectedDevices.length > 0 &&
                  selectedDevices.length < filteredEntities.length
                }
                checked={
                  selectedDevices.length === filteredEntities.length &&
                  filteredEntities.length > 0
                }
                onChange={handleSelectAll}
                color="primary"
              />

              <Tooltip title="Move selected devices">
                <span style={{ pointerEvents: "auto" }}>
                  <IconButton
                    color="primary"
                    size="medium"
                    disabled={selectedDevices.length === 0}
                    onClick={() => {
                      setMoveDialogOpen(true);
                      setDeviceToMove(null); // Indicate batch move
                    }}
                  >
                    <SwapHorizIcon fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Cancel Multi-Select">
                <span style={{ pointerEvents: "auto" }}>
                  <IconButton
                    color="default"
                    size="medium"
                    onClick={handleCancelMultiSelect}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {rooms.map((room) => {
            const roomId = room.roomId;
            const roomData = devicesByRoom[roomId] || {
              roomId,
              roomName: room.roomName,
              devices: [],
            };

            return (
              <div key={roomId}>
                <Typography
                  variant="h6"
                  className="font-semibold mb-3 flex items-center gap-2"
                >
                  {roomData.roomName}
                  <Typography variant="caption" color="textSecondary">
                    ({roomData.devices.length}{" "}
                    {roomData.devices.length === 1 ? "device" : "devices"})
                  </Typography>
                </Typography>
                {roomData.devices.length > 0 ? (
                  <ResponsiveCardGrid
                    spacing={1.5}
                    breakpoints={{ xs: 2, sm: 3, md: 4 }}
                  >
                    {roomData.devices.map((entity) => {
                      const deviceKey = `${entity.integration}:${entity.id}`;
                      const isSelected = selectedDevices.includes(deviceKey);
                      return (
                        <EntityCard
                          key={entity.id}
                          meta={meta}
                          entity={entity}
                          getComponentBgColor={getComponentBgColor}
                          onSelect={setSelectedEntity}
                          onEnterMultiSelect={handleEnterMultiSelect}
                          onShowMoveDialog={handleShowMoveDialog}
                          isMultiSelectMode={isMultiSelectMode}
                          isSelected={isSelected}
                          onCheckboxChange={handleCheckboxChange}
                          onLongPress={() => handleCardLongPress(entity)}
                          isShaking={shakingCardId === deviceKey}
                        />
                      );
                    })}
                  </ResponsiveCardGrid>
                ) : (
                  <></>
                )}
              </div>
            );
          })}

          {/* Unassigned Section */}
          {devicesByRoom["no-room"] && (
            <div>
              <Typography
                variant="h6"
                className="font-semibold mb-3 flex items-center gap-2"
              >
                {devicesByRoom["no-room"].roomName}
                <Typography variant="caption" color="textSecondary">
                  ({devicesByRoom["no-room"].devices.length}{" "}
                  {devicesByRoom["no-room"].devices.length === 1
                    ? "device"
                    : "devices"}
                  )
                </Typography>
              </Typography>
              {devicesByRoom["no-room"].devices.length > 0 ? (
                <ResponsiveCardGrid
                  spacing={1.5}
                  breakpoints={{ xs: 2, sm: 3, md: 4 }}
                >
                  {devicesByRoom["no-room"].devices.map((entity) => {
                    const deviceKey = `${entity.integration}:${entity.id}`;
                    const isSelected = selectedDevices.includes(deviceKey);
                    return (
                      <EntityCard
                        key={entity.id}
                        meta={meta}
                        entity={entity}
                        getComponentBgColor={getComponentBgColor}
                        onSelect={setSelectedEntity}
                        onEnterMultiSelect={handleEnterMultiSelect}
                        onShowMoveDialog={handleShowMoveDialog}
                        isMultiSelectMode={isMultiSelectMode}
                        isSelected={isSelected}
                        onCheckboxChange={handleCheckboxChange}
                        onLongPress={() => handleCardLongPress(entity)}
                        isShaking={shakingCardId === deviceKey}
                      />
                    );
                  })}
                </ResponsiveCardGrid>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="italic"
                >
                  No unassigned devices
                </Typography>
              )}
            </div>
          )}
        </div>

        {/* Entity Details Dialog */}
        <EntityDetailsDialog
          meta={meta}
          open={Boolean(selectedEntity)}
          entity={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />

        {/* Move Dialog */}
        <Dialog
          open={moveDialogOpen}
          onClose={handleMoveDialogClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {deviceToMove
              ? `Move ${deviceToMove.name}`
              : `Move ${selectedDevices.length} device(s)`}
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-2 pt-2">
              {rooms.map((room) => {
                const currentRoomId = deviceToMove
                  ? deviceRoomMap[
                      `${deviceToMove.integration}:${deviceToMove.id}`
                    ]
                  : null;
                const isCurrentRoom = currentRoomId === room.roomId;

                return (
                  <Button
                    key={room.roomId}
                    variant={isCurrentRoom ? "contained" : "outlined"}
                    disabled={isCurrentRoom}
                    onClick={() => {
                      if (deviceToMove) {
                        handleMoveToRoom(room.roomId);
                      } else {
                        handleBatchMove(room.roomId);
                        handleMoveDialogClose();
                      }
                    }}
                    className="justify-start normal-case"
                  >
                    {room.roomName}
                    {isCurrentRoom && (
                      <Chip label="Current" size="small" className="ml-2 h-5" />
                    )}
                  </Button>
                );
              })}
              <Button
                variant="outlined"
                onClick={() => {
                  if (deviceToMove) {
                    handleMoveToRoom(null);
                  } else {
                    handleBatchMove(null);
                    handleMoveDialogClose();
                  }
                }}
                className="justify-start normal-case"
              >
                Unassigned
              </Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleMoveDialogClose}
              className="normal-case"
              variant="outlined"
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default DeviceListView;
